import React, { useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const KpiGlowCard = ({ title, value, delta, deltaLabel, sub, color = '#00C853', icon: Icon, unit = '', loading = false, tooltip, 'data-testid': testId }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v).toLocaleString('es-ES'));

  useEffect(() => {
    if (typeof value === 'number' && !loading) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const controls = animate(count, value, { duration: 1.5, ease: 'easeOut' });
      return controls.stop;
    }
  }, [value, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const glowStyle = {
    boxShadow: `0 0 0 1px ${color}28, 0 0 30px ${color}18, 0 10px 30px rgba(0,0,0,0.25)`,
    borderColor: `${color}38`,
  };

  if (loading) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl border p-5 animate-pulse"
        style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
        data-testid="kpi-glow-card-loading"
      >
        <div className="h-3 w-20 rounded mb-4" style={{ background: 'var(--vz-bg-elevated)' }} />
        <div className="h-10 w-24 rounded" style={{ background: 'var(--vz-bg-elevated)' }} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="relative overflow-hidden rounded-2xl border p-5 cursor-default select-none"
      style={{ background: 'var(--vz-bg-card)', ...glowStyle }}
      data-testid={testId || 'kpi-glow-card'}
    >
      {/* Glow accent */}
      <div
        className="absolute -top-6 -left-6 w-24 h-24 rounded-full blur-2xl"
        style={{ background: color, opacity: 0.12 }}
      />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <p
              className="font-montserrat text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ color: 'var(--vz-text-secondary)' }}
            >
              {title}
            </p>
            {tooltip && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={11} style={{ color: 'var(--vz-text-muted)', cursor: 'help', flexShrink: 0 }} />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[220px] text-xs leading-snug"
                    style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}
                  >
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {Icon && (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
              <Icon size={16} style={{ color }} />
            </div>
          )}
        </div>

        <div className="flex items-end gap-2">
          <motion.span
            className="font-figtree text-3xl lg:text-4xl font-bold tabular-nums"
            style={{ color }}
          >
            {typeof value === 'number' ? rounded : (value ?? '—')}
          </motion.span>
          {unit && <span className="text-sm mb-1" style={{ color: 'var(--vz-text-secondary)' }}>{unit}</span>}
          {delta !== undefined && delta !== null && (
            <span
              className="text-sm font-medium mb-1"
              style={{ color: delta >= 0 ? '#00C853' : '#FF5252' }}
            >
              {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
            </span>
          )}
        </div>
        {(sub || deltaLabel) && (
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {sub && <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{sub}</p>}
            {deltaLabel && (
              <span className="text-[10px] font-medium" style={{ color: delta != null && delta >= 0 ? '#00C853' : '#FF5252' }}>
                {deltaLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Animated border bottom */}
      <motion.div
        className="absolute bottom-0 left-0 h-0.5"
        initial={{ width: '0%' }}
        animate={{ width: '100%' }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
    </motion.div>
  );
};
